if (!customElements.get('product-tabs')) {
  class ProductTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('.js-pt-tab')];
      this.panels = [...this.querySelectorAll('.js-pt-panel')];
      this.viewAll = this.querySelector('.js-pt-view-all');
      this.prevBtn = this.querySelector('.js-pt-prev');
      this.nextBtn = this.querySelector('.js-pt-next');
      this.rtl = document.dir === 'rtl';
      this.activeIndex = Math.max(0, this.panels.findIndex((p) => !p.hidden));

      this.update = this.update.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.activate(i));
        tab.addEventListener('keydown', (evt) => this.onTabKeydown(evt, i));
      });
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

      this.panels.forEach((panel) => {
        const track = panel.querySelector('.js-pt-track');
        if (!track) return;
        track.addEventListener('scroll', this.onScroll, { passive: true });
        if (window.matchMedia('(pointer: fine)').matches) this.initDrag(track);
      });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this);
      } else {
        window.addEventListener('resize', this.update);
      }

      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.update();
      this.iniciarCargaDiferida();
    }

    /**
     * Calimod (CADS, 9-oct-2026): paneles con data-pt-url llegan con un esqueleto del mismo alto y piden sus
     * tarjetas despues. Si el panel visible esta diferido ("al acercarse"), se pide cuando la seccion esta a ~800 px;
     * los ocultos, con la pagina ya cargada o al pasar el mouse / tocar su pestana.
     */
    iniciarCargaDiferida() {
      this.cargas = new Map();
      const diferido = (i) => !!(this.panels[i] && this.panels[i].querySelector('.js-pt-track[data-pt-url]'));
      if (!this.panels.some((p, i) => diferido(i))) return;

      this.tabs.forEach((tab, i) => {
        ['mouseenter', 'focus', 'touchstart'].forEach((ev) => {
          tab.addEventListener(ev, () => this.cargarPanel(i), { passive: true });
        });
      });

      const todas = () => this.panels.forEach((p, i) => this.cargarPanel(i));
      const enReposo = () => (window.requestIdleCallback ? window.requestIdleCallback(todas, { timeout: 4000 }) : setTimeout(todas, 1500));
      const trasCargar = () => {
        if (document.readyState === 'complete') enReposo();
        else window.addEventListener('load', enReposo, { once: true });
      };

      if (diferido(this.activeIndex) && 'IntersectionObserver' in window) {
        // Se empieza a mirar con la pagina ya cargada, para no competir con el banner principal
        const observar = () => {
          const io = new IntersectionObserver((entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            io.disconnect();
            this.cargarPanel(this.activeIndex).then(trasCargar);
          }, { rootMargin: '800px 0px' });
          io.observe(this);
        };
        if (document.readyState === 'complete') observar();
        else window.addEventListener('load', observar, { once: true });
      } else {
        trasCargar();
      }
    }

    cargarPanel(index) {
      const panel = this.panels[index];
      const track = panel ? panel.querySelector('.js-pt-track[data-pt-url]') : null;
      if (!track) return Promise.resolve();
      if (this.cargas.has(index)) return this.cargas.get(index);
      const promesa = fetch(track.dataset.ptUrl, { credentials: 'same-origin' })
        .then((r) => {
          if (!r.ok) throw new Error(String(r.status));
          return r.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const items = [...doc.querySelectorAll('.pt-tarjetas > .product-tabs__item')];
          if (!items.length) throw new Error('sin productos');
          track.replaceChildren(...items.map((n) => document.importNode(n, true)));
          track.removeAttribute('data-pt-url');
          if (index === this.activeIndex) this.update();
        })
        .catch(() => {
          this.cargas.delete(index);
        });
      this.cargas.set(index, promesa);
      return promesa;
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.update);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    get track() {
      const panel = this.panels[this.activeIndex];
      return panel ? panel.querySelector('.js-pt-track') : null;
    }

    get items() {
      return this.track ? [...this.track.children] : [];
    }

    get span() {
      const { items } = this;
      if (items.length > 1) return Math.abs(items[1].offsetLeft - items[0].offsetLeft);
      return items[0] ? items[0].offsetWidth : 0;
    }

    /**
     * Activa una pestaña: muestra su panel, actualiza "Ver todo" y las flechas.
     * @param {number} index - Índice de la pestaña.
     * @param {boolean} focus - Mover el foco a la pestaña.
     */
    activate(index, focus = false) {
      if (index === this.activeIndex || !this.panels[index]) return;
      if (this.cargas) this.cargarPanel(index);

      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      });

      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
        panel.classList.remove('is-entering');
      });

      const panel = this.panels[index];
      // Reinicia la animación de entrada.
      void panel.offsetWidth; // eslint-disable-line no-void
      panel.classList.add('is-entering');

      this.activeIndex = index;
      if (this.track) this.track.scrollLeft = 0;

      if (this.viewAll && this.tabs[index] && this.tabs[index].dataset.url) {
        this.viewAll.href = this.tabs[index].dataset.url;
      }

      if (focus && this.tabs[index]) {
        this.tabs[index].focus();
        this.tabs[index].scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      this.update();
    }

    onTabKeydown(evt, index) {
      let next = null;
      if (evt.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
      if (evt.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
      if (evt.key === 'Home') next = 0;
      if (evt.key === 'End') next = this.tabs.length - 1;
      if (next === null) return;
      evt.preventDefault();
      if (this.rtl && (evt.key === 'ArrowRight' || evt.key === 'ArrowLeft')) {
        next = evt.key === 'ArrowRight'
          ? (index - 1 + this.tabs.length) % this.tabs.length
          : (index + 1) % this.tabs.length;
      }
      this.activate(next, true);
    }

    update() {
      const { track } = this;
      if (!track) return;
      const maxScroll = track.scrollWidth - track.clientWidth;
      this.toggleAttribute('data-static', maxScroll <= 2);
      this.setButtonStates();
    }

    setButtonStates() {
      const { track } = this;
      if (!track) return;
      const pos = Math.abs(track.scrollLeft);
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (this.prevBtn) this.prevBtn.disabled = pos <= 2;
      if (this.nextBtn) this.nextBtn.disabled = pos >= maxScroll - 2;
    }

    onScroll(evt) {
      if (evt.currentTarget !== this.track || this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.setButtonStates();
        this.ticking = false;
      });
    }

    get currentIndex() {
      const { track, span } = this;
      if (!track || !span) return 0;
      return Math.round(Math.abs(track.scrollLeft) / span);
    }

    goTo(index) {
      const { track, span } = this;
      if (!track) return;
      const left = Math.max(0, index) * span;
      track.scrollTo({ left: this.rtl ? -left : left, behavior: 'smooth' });
    }

    step(direction) {
      this.goTo(this.currentIndex + direction);
    }

    onBlockSelect(evt) {
      const index = this.tabs.findIndex((tab) => tab === evt.target || tab.contains(evt.target));
      if (index > -1) this.activate(index);
    }

    /** Arrastrar con el mouse en escritorio. */
    initDrag(track) {
      let startX = 0;
      let startScroll = 0;
      let down = false;
      let moved = false;
      const blockClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
      };

      track.addEventListener('dragstart', (evt) => evt.preventDefault());
      track.addEventListener('mousedown', (evt) => {
        if (evt.button !== 0 || this.hasAttribute('data-static')) return;
        down = true;
        moved = false;
        startX = evt.pageX;
        startScroll = track.scrollLeft;
      });
      track.addEventListener('mousemove', (evt) => {
        if (!down) return;
        const dx = evt.pageX - startX;
        if (!moved && Math.abs(dx) > 8) {
          moved = true;
          track.classList.add('is-dragging');
        }
        if (moved) track.scrollLeft = startScroll - dx;
      });
      const end = (evt) => {
        if (!down) return;
        down = false;
        if (!moved) return;
        track.classList.remove('is-dragging');
        this.goTo(this.currentIndex);
        if (evt.type === 'mouseup') {
          track.addEventListener('click', blockClick, { capture: true, once: true });
          setTimeout(() => track.removeEventListener('click', blockClick, { capture: true }), 100);
        }
      };
      track.addEventListener('mouseup', end);
      track.addEventListener('mouseleave', end);
    }
  }

  customElements.define('product-tabs', ProductTabs);
}
